import { SocialIcon } from "react-social-icons";
import { CodeBlock } from "./uiComponents.tsx";
import { conversationsRoute, isDarkMode } from "./routes.tsx";
import { useLocation } from "preact-iso";
import { primaryColor, t } from "./theme.ts";
import {
  Activity,
  BarChart3,
  FileText,
  FolderOpen,
  Lock,
  Search,
  Smartphone,
  Zap,
} from "lucide-preact";
// --- Data ---

const apiUsage = (token: string) =>
  `fetch("https://api.view-chat.com", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    endpoint: "log",
    token: "${token}",
    payload: {
      event: {
        time: Date.now(),
        from: "support-bot",
        text: "How can I help you today?",
      },
      conversation: "ticket-4821",
      groups: ["customer-support"],
    },
  }),
});`;

type Feature = {
  // deno-lint-ignore no-explicit-any
  icon: any;
  title: string;
  description: string;
};

const features: Feature[] = [
  {
    icon: Activity,
    title: "Live monitoring",
    description:
      "Stream conversations in real time. No polling, no page refreshes.",
  },
  {
    icon: Search,
    title: "Full-text search",
    description:
      "Instant search across all messages. Filter by participant, date, or keyword.",
  },
  {
    icon: FolderOpen,
    title: "Group organization",
    description:
      "Organize conversations by team, project, or topic. Granular viewer permissions per group.",
  },
  {
    icon: Lock,
    title: "Automatic redaction",
    description:
      "API keys, tokens, and secrets are detected and redacted before storage. No configuration needed.",
  },
  {
    icon: FileText,
    title: "Quality reports",
    description:
      "Automated AI-generated reports analyze sampled conversations against your interests on a schedule.",
  },
  {
    icon: BarChart3,
    title: "Analytics",
    description:
      "Track message volume, active conversations, and participant activity over time.",
  },
  {
    icon: Smartphone,
    title: "Mobile ready",
    description:
      "Responsive interface that works on any screen size. Monitor from anywhere.",
  },
  {
    icon: Zap,
    title: "Single API call",
    description:
      "One POST request to log a message. No SDKs, no dependencies, no setup complexity.",
  },
];

// --- Components ---

const FeatureCard = ({ icon: Icon, title, description }: Feature) => {
  const th = t();
  return (
    <div
      style={{
        padding: "28px 24px",
        background: th.cardBg,
        border: `1px solid ${th.border}`,
        borderRadius: 8,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          background: th.primaryBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: primaryColor(),
        }}
      >
        <Icon size={18} strokeWidth={1.8} />
      </div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 600,
          color: th.textBright,
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: 14,
          lineHeight: "1.55",
          color: th.textMuted,
        }}
      >
        {description}
      </div>
    </div>
  );
};

const CTAButton = () => {
  const route = useLocation().route;
  const pc = primaryColor();
  const isDark = isDarkMode.value;
  return (
    <button
      type="button"
      onClick={() => route(conversationsRoute)}
      style={{
        background: pc,
        color: isDark ? "#0a0a0a" : "#ffffff",
        fontWeight: 600,
        fontSize: 15,
        padding: "10px 28px",
        borderRadius: 6,
        border: "none",
        cursor: "pointer",
        letterSpacing: "-0.01em",
        transition: "opacity 0.15s",
      }}
      onMouseOver={(e) => (e.currentTarget.style.opacity = "0.85")}
      onMouseOut={(e) => (e.currentTarget.style.opacity = "1")}
    >
      Get started
    </button>
  );
};

const SectionLabel = ({ text }: { text: string }) => (
  <div
    style={{
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: primaryColor(),
      marginBottom: 12,
    }}
  >
    {text}
  </div>
);

const Hero = () => {
  const th = t();
  return (
    <section
      style={{
        maxWidth: 720,
        margin: "0 auto",
        padding: "72px 24px 48px",
        textAlign: "center",
      }}
    >
      <SectionLabel text="Conversation monitoring" />
      <h1
        style={{
          fontSize: "clamp(1.75rem, 4vw, 2.75rem)",
          fontWeight: 700,
          color: th.textBright,
          lineHeight: 1.15,
          letterSpacing: "-0.025em",
          marginBottom: 16,
        }}
      >
        Observe every conversation your AI agents have
      </h1>
      <p
        style={{
          fontSize: "clamp(1rem, 2vw, 1.15rem)",
          color: th.textMuted,
          lineHeight: 1.6,
          maxWidth: 540,
          margin: "0 auto 32px",
        }}
      >
        Log, search, and analyze chatbot conversations in real time. Automatic
        secret redaction and AI-powered quality reports included.
      </p>
      <CTAButton />
    </section>
  );
};

const Features = () => (
  <section
    style={{
      maxWidth: 960,
      margin: "0 auto",
      padding: "32px 24px 48px",
    }}
  >
    <div
      style={{
        display: "grid",
        gap: 16,
        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
      }}
    >
      {features.map((f) => <FeatureCard key={f.title} {...f} />)}
    </div>
  </section>
);

const Integration = () => {
  const th = t();
  return (
    <section
      style={{
        maxWidth: 640,
        margin: "0 auto",
        padding: "32px 24px 48px",
      }}
    >
      <SectionLabel text="Integration" />
      <h2
        style={{
          fontSize: "clamp(1.25rem, 3vw, 1.75rem)",
          fontWeight: 700,
          color: th.textBright,
          letterSpacing: "-0.02em",
          marginBottom: 8,
        }}
      >
        One HTTP call. That's it.
      </h2>
      <p
        style={{
          fontSize: 15,
          color: th.textMuted,
          lineHeight: 1.6,
          marginBottom: 24,
        }}
      >
        No SDKs to install, no agents to configure. Send a POST request with
        your message data and ViewChat handles the rest.
      </p>
      <div
        style={{
          border: `1px solid ${th.border}`,
          borderRadius: 8,
          background: th.cardBg,
          padding: 16,
          overflow: "auto",
        }}
      >
        <CodeBlock code={apiUsage("YOUR_TOKEN")} />
      </div>
    </section>
  );
};

const BottomCTA = () => {
  const th = t();
  return (
    <section
      style={{
        maxWidth: 640,
        margin: "0 auto",
        padding: "32px 24px 56px",
        textAlign: "center",
      }}
    >
      <h2
        style={{
          fontSize: "clamp(1.25rem, 3vw, 1.75rem)",
          fontWeight: 700,
          color: th.textBright,
          letterSpacing: "-0.02em",
          marginBottom: 12,
        }}
      >
        Start monitoring in minutes
      </h2>
      <p
        style={{
          fontSize: 15,
          color: th.textMuted,
          lineHeight: 1.6,
          marginBottom: 24,
        }}
      >
        Free to get started. No credit card required.
      </p>
      <CTAButton />
    </section>
  );
};

const Footer = () => {
  const th = t();
  return (
    <footer
      style={{
        maxWidth: 960,
        margin: "0 auto",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 12,
        padding: "20px 24px",
        borderTop: `1px solid ${th.border}`,
        fontSize: 13,
        color: th.textFaint,
      }}
    >
      <div>&copy; {new Date().getFullYear()} ViewChat</div>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <SocialIcon
          url="https://discord.gg/4taKG4KTVJ"
          bgColor="transparent"
          fgColor={th.textFaint}
          style={{ height: 28, width: 28 }}
        />
        <a
          href="/terms"
          style={{ color: th.textFaint, textDecoration: "none" }}
          onMouseOver={(e) => (e.currentTarget.style.color = th.textMuted)}
          onMouseOut={(e) => (e.currentTarget.style.color = th.textFaint)}
        >
          Terms
        </a>
        <a
          href="/privacy"
          style={{ color: th.textFaint, textDecoration: "none" }}
          onMouseOver={(e) => (e.currentTarget.style.color = th.textMuted)}
          onMouseOut={(e) => (e.currentTarget.style.color = th.textFaint)}
        >
          Privacy
        </a>
      </div>
    </footer>
  );
};

export const LandingPage = () => (
  <div className="landing-page-root">
    <Hero />
    <Features />
    <Integration />
    <BottomCTA />
    <Footer />
  </div>
);
